import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { AnalysisStepsState, PpiAnalysisState, initialPpiAnalysisState, analysisSteps as stepDefinitions, SimilarCar } from '../../../types';
import { VehicleDetails } from '../../../types/vehicle';
import { authPpiApiAxiosWithRefresh } from '../../../services/api';

export const PpiAnalysisContext = createContext<PpiAnalysisState>(initialPpiAnalysisState);

export const usePpiAnalysis = () => useContext(PpiAnalysisContext);

interface PpiAnalysisProviderProps {
  children: ReactNode;
  docId: string | null;
}

const getInitialStepsState = (): AnalysisStepsState => {
  const steps: AnalysisStepsState = {};
  stepDefinitions.forEach(step => {
    steps[step.key] = { status: 'pending' };
  });
  return steps;
};

export const PpiAnalysisProvider: React.FC<PpiAnalysisProviderProps> = ({ children, docId }) => {
  const [analysisStarted, setAnalysisStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisSteps, setAnalysisSteps] = useState<AnalysisStepsState>(getInitialStepsState());
  const [isComplete, setIsComplete] = useState(false);
  const [sentEmailAddress, setSentEmailAddress] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [similarCars, setSimilarCars] = useState<SimilarCar[]>([]);

  const resetState = useCallback(() => {
    setAnalysisStarted(false);
    setLoading(false);
    setError(null);
    setAnalysisSteps(getInitialStepsState());
    setIsComplete(false);
    setSentEmailAddress(null);
    setEmailError(null);
    setSimilarCars([]);
  }, []);

  const fetchSimilarCars = useCallback(async (vehicleDetails: VehicleDetails) => {
    if (vehicleDetails) {
      const { Make, Model, Year } = vehicleDetails;
      try {
        const similarCarsResponse = await (await authPpiApiAxiosWithRefresh()).post('/demo/getSimilarSold', {
          make: Make,
          model: Model,
          year: Year,
        });
        setSimilarCars(similarCarsResponse.data);
      } catch (err) {
        console.error("Failed to fetch similar cars:", err);
      }
    }
  }, []);

  useEffect(() => {
    const loadExistingAnalysis = async () => {
      if (!docId) return;

      setLoading(true);
      try {
        const response = await (await authPpiApiAxiosWithRefresh()).get(`/get-analysis-data/${docId}`);
        const data = await response.data;

        if (data.success) {
          setAnalysisSteps(data.analysisSteps);
          setSentEmailAddress(data.email || null);

          if (data.overallStatus === 'completed' || data.overallStatus === 'error') {
            setIsComplete(true);
            setAnalysisStarted(false);
            fetchSimilarCars(data.vehicleDetails); // Fetch cars for completed reports
          } else if (data.overallStatus === 'in_progress') {
            setAnalysisStarted(true);
            setIsComplete(false);
          } else {
            const hasStarted = Object.values(data.analysisSteps).some((s: any) => s.status !== 'pending');
            setAnalysisStarted(hasStarted);
            setIsComplete(false);
          }

        } else {
          setError(data.error || 'Failed to load existing analysis.');
        }
      } catch (err) {
        setError('Error loading existing analysis.');
        console.error('Error loading existing analysis:', err);
      } finally {
        setLoading(false);
      }
    };

    if (docId) {
      loadExistingAnalysis();
    }
  }, [docId, fetchSimilarCars]);

  const startAnalysis = useCallback(async (sendEmail: boolean) => {
    if (!docId) {
      setError('docId is not available.');
      return;
    }

    setLoading(true);
    setError(null);
    setAnalysisStarted(true);

    const inProgressSteps: AnalysisStepsState = {};
    stepDefinitions.forEach(step => {
      inProgressSteps[step.key] = { status: 'in_progress' };
    });
    setAnalysisSteps(inProgressSteps);

    try {
      const response = await (await authPpiApiAxiosWithRefresh()).get(`/demo/analysis-data/${docId}`);
      const analysisData = response.data;
      console.log("Full analysis data received:", analysisData);

      // Fetch similar cars
      fetchSimilarCars(analysisData.vehicleDetails);

      const steps = stepDefinitions;
      const completedSteps: AnalysisStepsState = {};

      steps.forEach(step => {
        const stepData = analysisData.analysisSteps[step.key];
        completedSteps[step.key] = { status: 'completed', data: stepData?.data };
      });

      setAnalysisSteps(completedSteps);

      console.log("Finished processing all steps.");
      setIsComplete(true);
      setLoading(false);
      if (sendEmail) {
        setSentEmailAddress(analysisData.email);
      }

      try {
        (async () => {
          await (await authPpiApiAxiosWithRefresh()).post(`/demo/mark-demo-complete/${docId}`);
          console.log("Successfully marked demo as complete on the backend.");
        })();
      } catch (err) {
        console.error("Failed to mark demo as complete on the backend:", err);
      }

    } catch (err) {
      setError('An error occurred during the demo analysis.');
      setLoading(false);
      console.error("Error in startAnalysis:", err);
    }
  }, [docId, fetchSimilarCars]);

  const value = {
    analysisStarted,
    loading,
    error,
    analysisSteps,
    isComplete,
    sentEmailAddress,
    emailError,
    similarCars,
    startAnalysis,
    resetState,
  };

  return (
    <PpiAnalysisContext.Provider value={value}>
      {children}
    </PpiAnalysisContext.Provider>
  );
};
